import Navbar from "../Navbar";

import { useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  TablePagination,
  Chip,
  Box,
  IconButton,
  Tooltip,
  TableSortLabel,
  Container,
} from "@mui/material"
import { Person, SupervisorAccount, AdminPanelSettings, Search, Refresh } from "@mui/icons-material"

// Datos de ejemplo
const autorizantesData = [
  {
    id: 1,
    nombre: "Carlos Rodríguez",
    nivel: "Básico",
    limiteCompra: 5000,
    fechaActualizacion: "2023-10-15",
  },
  {
    id: 2,
    nombre: "María González",
    nivel: "Intermedio",
    limiteCompra: 15000,
    fechaActualizacion: "2023-11-20",
  },
  {
    id: 3,
    nombre: "Juan Pérez",
    nivel: "Avanzado",
    limiteCompra: 50000,
    fechaActualizacion: "2023-12-05",
  },
  {
    id: 4,
    nombre: "Ana Martínez",
    nivel: "Administrador",
    limiteCompra: 100000,
    fechaActualizacion: "2024-01-10",
  },
  {
    id: 5,
    nombre: "Luis Sánchez",
    nivel: "Básico",
    limiteCompra: 5000,
    fechaActualizacion: "2023-09-30",
  },
  {
    id: 6,
    nombre: "Elena Torres",
    nivel: "Intermedio",
    limiteCompra: 15000,
    fechaActualizacion: "2023-11-15",
  },
  {
    id: 7,
    nombre: "Roberto Díaz",
    nivel: "Avanzado",
    limiteCompra: 50000,
    fechaActualizacion: null,
  },
  {
    id: 8,
    nombre: "Patricia López",
    nivel: "Administrador",
    limiteCompra: 100000,
    fechaActualizacion: "2024-02-01",
  },
]

// Función para obtener el color según el nivel de autorización
const getNivelColor = (nivel) => {
  switch (nivel) {
    case "Básico":
      return "#4caf50" // Verde
    case "Intermedio":
      return "#2196f3" // Azul
    case "Avanzado":
      return "#ff9800" // Naranja
    case "Administrador":
      return "#f44336" // Rojo
    default:
      return "#9e9e9e" // Gris
  }
}

// Función para obtener el icono según el nivel de autorización
const getNivelIcon = (nivel) => {
  switch (nivel) {
    case "Básico":
      return <Person />
    case "Intermedio":
      return <SupervisorAccount />
    case "Avanzado":
      return <SupervisorAccount style={{ color: "#ff9800" }} />
    case "Administrador":
      return <AdminPanelSettings />
    default:
      return <Person />
  }
}

// Componente principal
const AutorizantesTabla = () => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [searchTerm, setSearchTerm] = useState("")
  const [orderBy, setOrderBy] = useState("nombre")
  const [order, setOrder] = useState("asc")

  // Función para manejar el cambio de página
  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  // Función para manejar el cambio de filas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  // Función para manejar la búsqueda
  const handleSearch = (event) => {
    setSearchTerm(event.target.value)
    setPage(0)
  }

  // Función para manejar el ordenamiento
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc"
    setOrder(isAsc ? "desc" : "asc")
    setOrderBy(property)
  }

  // Función para crear un manejador de ordenamiento
  const createSortHandler = (property) => () => {
    handleRequestSort(property)
  }

  // Función para comparar valores al ordenar
  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1
    }
    if (b[orderBy] > a[orderBy]) {
      return 1
    }
    return 0
  }

  // Función para obtener el comparador
  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy)
  }

  // Función para filtrar y ordenar los datos
  const filteredData = autorizantesData
    .filter(
      (autorizante) =>
        autorizante.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        autorizante.nivel.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort(getComparator(order, orderBy))
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  // Formatear la moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    }).format(amount)
  }

  // Formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return "No actualizado"

    const date = new Date(dateString)
    return new Intl.DateTimeFormat("es-AR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date)
  }

  return (
    <Container maxWidth="xl">
    <Paper elevation={3} sx={{ width: "100%", overflow: "hidden", p: 2 }}>
    
    <Navbar/>
    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: "bold", color: "#1976d2" }}>
         Autorizantes Compras 
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Search sx={{ color: "action.active", mr: 1 }} />
        <TextField
          variant="outlined"
          size="small"
          label="Buscar autorizante"
          value={searchTerm}
          onChange={handleSearch}
          sx={{ flexGrow: 1 }}
        />
        <Tooltip title="Actualizar datos">
          <IconButton onClick={() => setSearchTerm("")}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 440, mb: 2 }}>
        <Table stickyHeader aria-label="tabla de autorizantes">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "nombre"}
                  direction={orderBy === "nombre" ? order : "asc"}
                  onClick={createSortHandler("nombre")}
                >
                  Usuario
                </TableSortLabel> 
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "nivel"}
                  direction={orderBy === "nivel" ? order : "asc"}
                  onClick={createSortHandler("nivel")}
                >
                  Nivel de Autorización
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "limiteCompra"}
                  direction={orderBy === "limiteCompra" ? order : "asc"}
                  onClick={createSortHandler("limiteCompra")}
                >
                  Límite de Compra
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "fechaActualizacion"}
                  direction={orderBy === "fechaActualizacion" ? order : "asc"}
                  onClick={createSortHandler("fechaActualizacion")}
                >
                  Última Actualización
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((autorizante) => (
              <TableRow key={autorizante.id} hover>
                <TableCell component="th" scope="row">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {getNivelIcon(autorizante.nivel)}
                    <Typography sx={{ ml: 1 }}>{autorizante.nombre}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={autorizante.nivel}
                    sx={{
                      backgroundColor: getNivelColor(autorizante.nivel),
                      color: "white",
                      fontWeight: "bold",
                    }}
                  />
                </TableCell>
                <TableCell>{formatCurrency(autorizante.limiteCompra)}</TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      color: autorizante.fechaActualizacion ? "text.primary" : "text.secondary",
                      fontStyle: autorizante.fechaActualizacion ? "normal" : "italic",
                    }}
                  >
                    {formatDate(autorizante.fechaActualizacion)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={autorizantesData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />
    </Paper>
    </Container>
  )
}

export default AutorizantesTabla
